import { useCallback, useEffect, useState } from 'react';
import PageTemplate from 'components/template/PageTemplate';
import Container from 'components/common/Container';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jsonAxios } from 'components/axios';
import { IAllFileList, IFileInfo, IStepList, ITransport, ITransportDetail } from 'types/transport';
import { ReactComponent as Dot } from 'assets/transport/detail/dot.svg'
import { ReactComponent as ProgressIcon } from 'assets/transport/detail/progress.svg'
import { ReactComponent as FileDel } from 'assets/common/fileDel.svg'
import { ReactComponent as WarnInfo } from 'assets/transport/detail/warnInfo.svg'
import { ReactComponent as Check } from 'assets/transport/detail/check.svg'
import CustomButton from 'components/common/CustomButton';
import { ReactComponent as Down } from 'assets/common/download.svg'
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import StepRender from 'components/transport/detail/stepRender';
import BookingSchedule from 'components/transport/detail/BookingSchedule';
import CargoInCargoInfo from 'components/transport/detail/CargoInCargoInfo';
import { toast } from 'react-toastify';
import First from 'components/transport/detail/First';
import { download } from 'components/function';
import useCheckCompany from 'hook/useCheckCompany';
import { useLocation } from 'react-router-dom';
import { useRive } from '@rive-app/react-canvas';
import ScoreDrawer from 'components/transport/drawer';
import { useGetUser } from 'atom/user';

const TransportDetail = () => {
    const user = useGetUser();
    const navigate = useNavigate();
    const [stepList, setStepList] = useState<IStepList[]>([
        { label: '화물출고', key: 'CARGO_OUT' },
        { label: '부킹', key: 'BOOKING' },
        { label: '수출통관', key: 'EXPORT_CUSTOMS' },
        { label: '서류마감', key: 'DOCUMENT_CUTOFF' },
        { label: '화물반입', key: 'CARGO_IN' },
        { label: '출항', key: 'DEPARTED' },
        { label: '운송중', key: 'IN_TRANSIT' },
        { label: '입항', key: 'ARRIVED' },
    ])

    const location = useLocation();
    const { stepName } = location.state || {};
    const [step, setStep] = useState<string>(stepName ? stepName : '');
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const [data, setData] = useState<ITransportDetail>({} as ITransportDetail);
    const [transportInfo, setTransportInfo] = useState<ITransport>({} as ITransport)
    const [allFileList, setAllFileList] = useState<any>([])
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [avgScore, setAvgScore] = useState<number>(0)
    const [color, setColor] = useState<string>('#4AD35E')
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const { rive, RiveComponent } = useRive({
        src: "/beat1.riv", // Rive 파일 경로
        stateMachines: 'State Machine 1',
        autoplay: true,
    });

    useCheckCompany();

    const init = useCallback(() => {
        if (!id) {
            toast.error('잘못된 경로입니다.');
            setTimeout(() => {
                navigate(-1);
            }, 500);
            return;
        }
        jsonAxios.get(`order/${id}`).then(res => {
            if (res.data.code === 200) {
                setTransportInfo(res.data.data)
                if (res.data.data.orderStepName !== '') {
                    setStep(res.data.data.orderStepName)
                }
                setIsOwner(true)
                setAvgScore(res.data.data?.scoreInfo?.total ?? 40)
            }
            else if (res.data.code === 400) {
                toast.error('존재하지 않는 주문번호입니다.')
                navigate('/transport/list', { replace: true })
            }
        })
    }, [id, navigate])

    useEffect(() => {
        init();
    }, [init])

    useEffect(() => {
        if (isOwner) {
            getStep()
            getDetail()
        }
    }, [isOwner, step])

    const updateStatus = (value: number) => {
        if (!value) return;
        if (rive) {
            const inputs = rive.stateMachineInputs("State Machine 1");
            if (inputs) {
                const input = inputs[0];

                if (value > 80) input.value = 0;
                else if (value > 60) input.value = 1;
                else input.value = 2;

                if (value > 80) setColor('#4AD35E');
                else if (value > 60) setColor('#F8A300');
                else setColor('#FF4D4D');
            }
        }
    };

    useEffect(() => {
        (avgScore && avgScore > 0 && rive) && updateStatus(avgScore)
    }, [avgScore, rive])

    const getStep = () => {
        const reqData = {
            orderId: id
        }
        jsonAxios.post(`order/step`, reqData).then(res => {
            if (res.data.code === 200) {
                const updated = stepList
                    .map(step => {
                        const status = res.data.data.stepList[step.key];
                        if (status !== undefined) {
                            return { ...step, status };
                        }
                        return null; // 제외할 항목
                    })
                    .filter(Boolean); // null 제거

                setStepList(updated as IStepList[]);
                if (step === '' && updated[0]) {
                    setStep(updated[0]?.key)
                }

                setAllFileList(res.data.data.allFileList)
            }
        })
    }

    const getDetail = () => {
        if (step === '') return
        setLoading(true)
        const reqData = {
            orderId: id,
            stepName: step
        }
        jsonAxios.post(`order/step/detail`, reqData).then(res => {
            if (res.data.code === 200) {
                setData(res.data.data)
            }
        }).finally(() => {
            setTimeout(() => {
                setLoading(false)
            }, 300)
        })
    }

    const downloadUserFilesAsZip = async (files: IFileInfo[] | undefined, zipName = 'files.zip') => {
        if (!files) return

        const zip = new JSZip();

        try {
            const fetchFile = async (file: IFileInfo, idx: number) => {
                const response = await fetch(file.filePath);
                const blob = await response.blob();
                zip?.file(`${idx + 1}_${file.name}`, blob); // 파일 추가
            };

            await Promise.all(files.map(fetchFile));

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, zipName);
        } catch (err) {
            console.log(err)
        }
    };

    const downloadAllFilesAsZip = async (files: IAllFileList, zipName = 'files.zip') => {
        const zip = new JSZip();

        const flatList: IFileInfo[] = Object.values(files).filter((files): files is IFileInfo[] => Array.isArray(files)).flat();

        try {
            const fetchFile = async (file: IFileInfo, idx: number) => {
                const response = await fetch(file.filePath);
                const blob = await response.blob();
                zip?.file(`${idx + 1}_${file.name}`, blob); // 파일 추가
            };

            await Promise.all(flatList.map(fetchFile));

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, zipName);
        } catch (err) {
            console.log(err)
        }
    };

    const cancelBooking = () => {
        jsonAxios.post(`order/${id}/cancel`).then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                toast.success('정상적으로 부킹취소되었습니다.')
                navigate(-1)
            }
            else if (res.data.code === 401) {
                toast.error('취소가능한 시간이 지났습니다.')
            }
        })
    }

    const delFile = (id: number) => {
        const reqData = {
            fileId: id
        }
        jsonAxios.post(`order/file`, reqData).then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                toast.success('정상적으로 삭제되었습니다.');
                init()
            }
        })
    }

    const goIssue = (id: number) => {
        navigate(`/transport/issue?id=${id}`)
    }

    const toggleDrawer = () => {
        setIsOpen((p) => !p)
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 mb-6 pt-21 h-full min-h-dvh">
                {/* {
                    user.useSolomon &&
                    transportInfo.transportType === 'SEA' &&
                    <div>
                        {
                            avgScore && avgScore > 0 &&
                            <div
                                className={`flex items-center justify-center gap-3 px-3 border rounded h-14 bg-white cursor-pointer max-w-[300px]`}
                                style={{ borderColor: color }}
                                onClick={() => {
                                    toggleDrawer()
                                }}
                            >
                                <div>
                                    <p className='font-semibold text-sm'>PULSE SCORE</p>
                                    <p className='text-[#9D9EB7] text-xs'>AVERAGE</p>
                                </div>
                                <Bar />
                                <div className='flex items-center gap-0.5'>
                                    <p
                                        className={`digital text-32`}
                                        style={{ color: color }}
                                    >
                                        {avgScore}
                                    </p>
                                    <div className='flex flex-col justify-center items-center gap-0.5 text-[#9D9EB7] text-xs'>
                                        <Heart fill={color} />
                                        <p className='leading-none'>BPM</p>
                                    </div>
                                </div>

                                <RiveComponent
                                    height={24}
                                    width={80}
                                    className='w-20 h-12'
                                />
                            </div>
                        }
                    </div>
                } */}
                <First transportInfo={transportInfo} />
                <div className='bg-white border-[#9D9EB7] border-1 rounded-lg overflow-hidden'>
                    <div className='bg-[#F8F8FA] px-10 py-6 font-semibold'>
                        <p className='text-2xl'>운송단계</p>
                    </div>

                    <div className='flex justify-center items-center gap-6 px-10 py-6 text-base'>
                        {
                            stepList.map((item, idx) => (
                                <div
                                    className='flex flex-col gap-6 w-full'
                                    key={idx}>
                                    <div
                                        className={`flex items-center justify-between h-12.5 border rounded-lg w-full px-4 transition-all hover:shadow-lg cursor-pointer text-xl
                                        ${item.status === 1 ?
                                                'text-white bg-primary' :
                                                item.key === step ? 'text-primary border-primary' : 'text-[#9D9EB7]'
                                            }`}
                                        onClick={() => setStep(item.key)}
                                    >
                                        {
                                            item.status === 1 ?
                                                <Check /> :
                                                <Dot fill={item.key === step ? '#196bff' : '#fff'} />
                                        }
                                        {item.label}
                                    </div>

                                    <div>
                                        <div className='relative flex justify-center items-center h-6'>
                                            <div className={`absolute flex items-center justify-center w-6 h-6 rounded-full transition-all ${item.status === 1 ? 'bg-[#4AD35E] right-0' : 'bg-[#D9D9D9] left-0 '}`}>
                                                <ProgressIcon />
                                            </div>
                                            <div
                                                className={`w-full h-2 rounded-full ${item.status === 1 ? 'bg-[#4AD35E]' : 'bg-[#D9D9D9]'}`} />
                                        </div>
                                        <p className={`text-sm text-center ${item.status === 1 ? 'text-[#4AD35E]' : 'text-[#D9D9D9]'}`}>
                                            {item.status === 1 ? '완료' : '미진행'}
                                        </p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {
                    user.useSolomon &&
                    transportInfo?.issueList &&
                    transportInfo?.issueList.length > 0 &&
                    <div className='bg-white border-[#9D9EB7] border-1 rounded-lg'>
                        <div className='bg-issue px-10 py-6 rounded-t-lg text-white'>
                            <p className='font-semibold text-2xl'>운송이슈</p>
                        </div>
                        <div className='tablet:flex tablet:flex-col gap-x-10 gap-y-3 grid grid-cols-2 px-6 py-5'>
                            {
                                transportInfo.issueList.map((item, idx) => (
                                    <div
                                        className='flex justify-between items-center gap-2 px-3 border-1 rounded h-15'
                                        key={idx}
                                        onClick={() => goIssue(item.id)}
                                    >
                                        <WarnInfo className='shrink-0'
                                            // fill={item.isRead ? '#9D9EB7' : '#ED4040'}
                                            fill={'#ED4040'}
                                        />
                                        <p className='font-semibold text-sm'>{item.issueCode}</p>
                                        <p className='text-sm truncate'>{item.title}</p>
                                        <p
                                            className={`cursor-pointer shrink-0 ${false ? 'text-[#9D9EB7]' : 'text-issue'}`}

                                        >
                                            상세보기
                                        </p>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                }

                <div className='bg-white border-[#9D9EB7] border-1 rounded-lg'>
                    <div className='bg-[#F8F8FA] px-10 py-6 rounded-t-lg'>
                        <p className='font-semibold text-2xl'>상세정보</p>
                    </div>
                    {
                        StepRender &&
                        <StepRender
                            step={step}
                            detailInfo={data}
                            type={transportInfo.transportType}
                            transportInfo={transportInfo}
                            loading={loading}
                        />
                    }
                </div>
                {/* 차량정보 */}
                {
                    transportInfo.transportType === 'SEA' &&
                    transportInfo.shipmentMode?.includes('FCL') &&
                    step === 'CARGO_IN' &&
                    data?.cargoInfo?.length > 0 &&
                    data?.cargoInfo[0].id &&
                    <CargoInCargoInfo
                        detailInfo={data}
                        type={transportInfo.transportType}
                        loading={loading}
                    />
                }

                {/* 스케줄정보 */}
                {
                    step === 'BOOKING' &&
                    <div className='bg-white border-[#9D9EB7] border-1 rounded-lg'>
                        <div className='bg-[#F8F8FA] px-10 py-6 rounded-t-lg'>
                            <p className='font-semibold text-2xl'>스케줄 정보</p>
                        </div>
                        <BookingSchedule
                            detailInfo={data}
                            type={transportInfo.transportType}
                            loading={loading}
                        />
                    </div>
                }
                {
                    (
                        Object.keys(allFileList).length > 0 ||
                        (transportInfo.userFileList && transportInfo.userFileList.length > 0)) &&
                    <div className='bg-white border border-[#9D9EB7] rounded-lg overflow-hidden'>
                        <div className='bg-[#F8F8FA] px-10 py-6 font-semibold'>
                            <p className='text-2xl'>서류정보</p>
                        </div>
                        <div className='gap-3 grid grid-cols-2 px-10 py-6'>
                            {
                                transportInfo.userFileList &&
                                transportInfo.userFileList.length > 0 &&
                                <div className='flex flex-col gap-4 w-full'>
                                    <p>첨부서류<span className='text-primary'>(회원)</span></p>
                                    <div className="flex flex-col h-full">
                                        {
                                            transportInfo.userFileList.map((file: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className={`relative text-primary hover:underline break-all text-sm border border-[#9D9EB7] px-10 py-3 text-center cursor-pointer
                                                    ${transportInfo.userFileList?.length === idx + 1 ? 'border-b' : 'border-b-0'}`}
                                                    onClick={() => {
                                                        download(file.filePath)
                                                    }}
                                                >
                                                    {file.name}
                                                    <FileDel
                                                        className='top-0 right-4 absolute flex justify-center items-center h-full'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            delFile(file.fileId)
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className='self-end'>
                                        <CustomButton
                                            className='bg-[#0ACF2E] !min-w-400 !h-10 !text-sm'
                                            Icon={Down}
                                            title='전체 다운로드'
                                            onClick={() => {
                                                downloadUserFilesAsZip(transportInfo.userFileList, `회원 서류.zip`)
                                            }}
                                        />
                                    </div>
                                </div>
                            }

                            {
                                allFileList &&
                                Object.keys(allFileList).length > 0 &&
                                <div className='flex flex-col gap-4 w-full'>
                                    <p>첨부서류<span className='text-primary'>(관리자)</span></p>
                                    <div className="flex flex-col border border-[#9D9EB7] h-full">
                                        {stepList
                                            .filter(({ key }) => {
                                                const files = allFileList?.[key as keyof typeof allFileList];
                                                return files && files.length > 0;
                                            })
                                            .map(({ key, label }, idx) => {
                                                const files = allFileList?.[key as keyof typeof allFileList];
                                                return (
                                                    <div key={key} className={`grid grid-cols-[120px_1fr] border-[#9D9EB7] 
                                                        ${idx !== 0 ? 'border-t' : ''}`}>
                                                        {/* 왼쪽 라벨 영역 */}
                                                        <div className={`flex justify-center items-center bg-[#EEF4FF] px-4 py-2 border-[#9D9EB7] border-r font-semibold text-lg`}>
                                                            {label}
                                                        </div>

                                                        {/* 오른쪽 파일 리스트 영역 */}
                                                        <div className="flex flex-col h-full">
                                                            {files.map((file: any, idx: number) => (
                                                                <a
                                                                    key={idx}
                                                                    href={file.filePath}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={`text-primary hover:underline break-all text-sm  border-[#9D9EB7] px-10 py-3 text-center ${idx !== files.length - 1 ? 'border-b' : ''}`}
                                                                >
                                                                    {file.name}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                    <div className='self-end'>
                                        <CustomButton
                                            className='bg-[#0ACF2E] !min-w-400 !h-10 !text-sm'
                                            Icon={Down}
                                            title='전체 다운로드'
                                            onClick={() => {
                                                downloadAllFilesAsZip(allFileList, `관리자 서류.zip`)
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }

                {
                    transportInfo.cancelAvail &&
                    <div className='flex flex-col justify-center items-center gap-6'>
                        <CustomButton
                            className='hover:!bg-white !border-red w-[200px] !text-red'
                            onClick={cancelBooking}
                            title='부킹취소'
                        />
                        {
                            transportInfo.cutOff &&
                            <div className='flex gap-3 bg-white p-4.5 border border-[#9D9EB7] rounded-lg'>
                                <p className='text-red'>부킹 취소 가능 기간 :</p>
                                <p>{transportInfo.cutOff}까지</p>
                            </div>
                        }
                    </div>
                }
            </Container>

            <ScoreDrawer
                isOpen={isOpen}
                toggleDrawer={toggleDrawer}
                avgScore={avgScore}
                id={Number(id)}
            />

        </PageTemplate>
    )
}

export default TransportDetail